import { useEffect, useMemo, useRef, useState } from 'react'

import { useFunnel } from '@onl-web/shared/hooks'

import Spinner from '@/components/Spinner'
import { NETWORK_ERROR_DESCRIPTION } from '@/constants'
import { useGetQuizzes } from '@/query/quiz/useGetQuizzes'
import { usePostQuizSubmit } from '@/query/quiz/usePostQuizSubmit'
import type { QuizSubmitResponse } from '@/types/quiz'

import { pickRandomUnsolved } from './pickRandomUnsolved'
import QuestionStep from './QuestionStep'
import QuizResultStep from './QuizResultStep'
import type { TransitionState } from './styles'
import { LoadingWrapper, QuizContainer, ResultContainer } from './styles'

type QuizInnerStep = 'question' | 'result'

interface QuizInnerData {
  submitResult?: QuizSubmitResponse
}

const CORRECT_TRANSITION_DELAY_MS = 500
const WRONG_TRANSITION_DELAY_MS = 550

interface QuizStepProps {
  onBack: (showEnergyToast?: boolean) => void
  onError?: (title: string, description?: string) => void
}

export default function QuizStep({ onBack, onError }: QuizStepProps) {
  const { step, data, setStep } = useFunnel<QuizInnerStep, QuizInnerData>('question')
  const { data: quizListData, isLoading, isError } = useGetQuizzes()
  const [transitionState, setTransitionState] = useState<TransitionState>('idle')
  const [submitErrorCount, setSubmitErrorCount] = useState(0)
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    return () => clearTimeout(transitionTimerRef.current)
  }, [])

  const unsolvedQuiz = useMemo(
    () => (quizListData ? pickRandomUnsolved(quizListData.quizzes) : null),
    [quizListData]
  )
  const [displayQuiz, setDisplayQuiz] = useState(unsolvedQuiz)
  if (transitionState === 'idle' && unsolvedQuiz && unsolvedQuiz.id !== displayQuiz?.id) {
    setDisplayQuiz(unsolvedQuiz)
  }

  const isMonthlyLimitReached = quizListData
    ? quizListData.solvedCount >= quizListData.monthlyQuizLimit
    : false

  const submitMutation = usePostQuizSubmit(displayQuiz?.id ?? 0)

  const handleAnswer = (selectedOptionIndex: number) => {
    if (!displayQuiz) return
    if (isMonthlyLimitReached) return

    submitMutation.mutate(
      { selectedOptionIndex },
      {
        onSuccess: (result) => {
          setTransitionState(result.isCorrect ? 'correct' : 'wrong')

          const delay = result.isCorrect ? CORRECT_TRANSITION_DELAY_MS : WRONG_TRANSITION_DELAY_MS
          transitionTimerRef.current = setTimeout(() => {
            setStep('result', { submitResult: result })
          }, delay)
        },
        onError: () => {
          setTransitionState('idle')
          setSubmitErrorCount((prev) => prev + 1)
          onError?.('퀴즈 제출에 실패했어요', NETWORK_ERROR_DESCRIPTION)
        },
      }
    )
  }

  useEffect(() => {
    if (isError) {
      onError?.('퀴즈를 불러오지 못했어요', NETWORK_ERROR_DESCRIPTION)
      onBack()
      return
    }
    if (!isLoading && (isMonthlyLimitReached || !unsolvedQuiz)) {
      onBack()
    }
  }, [isLoading, isError, unsolvedQuiz, isMonthlyLimitReached, onBack, onError])

  if (isLoading) {
    return (
      <QuizContainer>
        <LoadingWrapper>
          <Spinner size='medium' />
        </LoadingWrapper>
      </QuizContainer>
    )
  }

  if (!displayQuiz) return null

  if (step === 'result' && data.submitResult) {
    return (
      <ResultContainer>
        <QuizResultStep
          isCorrect={data.submitResult.isCorrect}
          resultText={data.submitResult.resultText}
          onBack={onBack}
        />
      </ResultContainer>
    )
  }

  return (
    <QuizContainer transitionState={transitionState}>
      <QuestionStep
        key={submitErrorCount}
        quiz={displayQuiz}
        onAnswer={handleAnswer}
        isSubmitting={submitMutation.isPending}
      />
    </QuizContainer>
  )
}
